import { Fab } from '@biq/apex-ui';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FormHelperText, Grid, Typography } from '@mui/material';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { DictionaryAttribute } from 'lib';
import React, { useEffect, useState } from 'react';
import { DictionaryEmptyStateIcon } from '../../../../assets';
import { EmptyStateContainer } from '../../../../components';
import {
    attributeStyles,
    AttributeTreeViewProps,
    CustomCollapseIcon,
    CustomExpandIcon,
    possiblyPopulateNodeAttributes,
} from './attribute-helper';
import { AttributeHighlights } from './AttributeHighlights';
import { MemoizedAttributeTreeItem } from './AttributeTreeItem';

const AttributeTreeView = ({
    onNodeSelected,
    handleAddAttribute,
    attributes,
    uniqueAttributeTypes,
    defaultExpandedIds,
    isErrorState,
}: AttributeTreeViewProps): React.JSX.Element => {
    const [expandedNodeInfoId, setExpandedNodeInfoId] = useState<string>('');
    const [selectedNodeId, setSelectedNodeId] = useState<string>('');
    const [expandedNodes, setExpandedNodes] =
        useState<string[]>(defaultExpandedIds);
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

    useEffect(() => {
        setSelectedNodeId(expandedNodeInfoId);
    }, [expandedNodeInfoId]);

    const handleToggle = (_: any, attribsParams: string[]): void => {
        if (!attribsParams.length) {
            setExpandedNodes([]);
            return;
        }
        const expandedNodeIds = expandedNodes.includes(attribsParams[0])
            ? expandedNodes.filter((nodeId) => attribsParams.includes(nodeId))
            : [...expandedNodes, selectedNodeId];

        setExpandedNodes(expandedNodeIds);
    };

    const handleFilterChange = (
        filter: string,
        action: 'add' | 'remove'
    ): void => {
        if (action === 'add') {
            setSelectedFilters((prevFilters) => [...prevFilters, filter]);
        } else {
            setSelectedFilters((prevFilters) =>
                prevFilters.filter((f) => f !== filter)
            );
        }
    };

    function shouldHighlight(attribute: any): boolean {
        const dataType = attribute.datatype;
        const modifiedKey = dataType
            .replace('[]', '')
            .replace(/^(.)(.*)$/, (currentVal: string) =>
                currentVal.toLowerCase()
            );

        const formattedFilters = selectedFilters.map((filter) =>
            filter.replace('[]', '_array')
        );
        return formattedFilters.includes(modifiedKey);
    }

    const handleNodeSelect = (_: any, nodeId: string): void => {
        setSelectedNodeId(nodeId);
    };

    return (
        <Grid sx={attributeStyles.boxStyle}>
            <Grid sx={attributeStyles.boxNestedStyle}>
                <Grid sx={attributeStyles.treeViewContainerStyles}>
                    <Grid sx={attributeStyles.fabButtonContainerStyles}>
                        <Fab
                            type='button'
                            sx={attributeStyles.fabButtonStyles}
                            icon={<AddIcon />}
                            color='secondary'
                            label='Attribute'
                            onClick={handleAddAttribute}
                        />
                        {isErrorState && (
                            <FormHelperText
                                variant='error'
                                sx={attributeStyles.errorStateContainer}
                            >
                                <ErrorOutlineIcon
                                    sx={attributeStyles.errorStateIcon}
                                />
                                <Typography sx={attributeStyles.errorStateText}>
                                    Object must have at least one child item.
                                </Typography>
                            </FormHelperText>
                        )}
                    </Grid>
                    <TreeView
                        defaultCollapseIcon={<CustomExpandIcon />}
                        defaultExpandIcon={<CustomCollapseIcon />}
                        expanded={expandedNodes}
                        onNodeToggle={handleToggle}
                        sx={attributeStyles.treeView}
                        selected={selectedNodeId}
                        onNodeSelect={handleNodeSelect}
                    >
                        {attributes?.length ? (
                            React.Children.toArray(
                                attributes?.map((a: DictionaryAttribute) => {
                                    const updatedAttribute =
                                        possiblyPopulateNodeAttributes(a);

                                    return (
                                        <MemoizedAttributeTreeItem
                                            key={updatedAttribute.id}
                                            setExpandedNodes={setExpandedNodes}
                                            attribute={updatedAttribute}
                                            nodeId={updatedAttribute.id}
                                            onNodeSelected={(): void =>
                                                onNodeSelected(updatedAttribute)
                                            }
                                            shouldHighlight={shouldHighlight}
                                            expandedNodeInfoId={
                                                expandedNodeInfoId
                                            }
                                            setExpandedNodeInfoId={
                                                setExpandedNodeInfoId
                                            }
                                        />
                                    );
                                })
                            )
                        ) : (
                            <EmptyStateContainer
                                emptyStateBodyMessage={
                                    'Create attributes using the button in the top left of this panel.'
                                }
                                emptyStateIcon={DictionaryEmptyStateIcon}
                            />
                        )}
                    </TreeView>
                </Grid>
            </Grid>
            <Grid sx={attributeStyles.boxNestedSecStyle}>
                <AttributeHighlights
                    uniqueAttributeTypes={uniqueAttributeTypes}
                    selectedFilters={selectedFilters}
                    onFilterChange={handleFilterChange}
                />
            </Grid>
        </Grid>
    );
};

export const MemoAttributeTreeView = React.memo(AttributeTreeView);
